import React, { useId } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Flex, Heading, Label, Switch, Text, TextLink } from '@indeed/ifl-components';
import { FileDoc, NewWindow } from '@indeed/ifl-icons';
import GettextFactory from '../../../util/GettextFactory';
import { useEventLogger } from '../../../lib/eventLogger/EventLoggerProvider';

export interface ApplicationQuestionsSectionProps {
    /** Whether the SQ (screening question) prefill toggle is on. Default ON. */
    sqOptedIn: boolean;
    /** Updates the local SQ toggle state. Persisted through updateAutoApply on Update. */
    onSqOptInChange: (checked: boolean) => void;
    /** Whether the DQ (voluntary self-identification) prefill toggle is on. Default OFF. */
    dqOptedIn: boolean;
    /** Updates the local DQ toggle state. Persisted through setDqPrefillConsent on Update. */
    onDqOptInChange: (checked: boolean) => void;
    /** Whether the DQ experiment exposes its preference control. */
    showDqToggle: boolean;
}

/**
 * Application questions section for the progressive-automation Review variant
 * (rnhp_auto_apply_banner_tst === 2). Renders two opt-in toggles whose changes
 * update local state only; ReviewPage persists each to its owning API on Update:
 * - SQ: "Autofill application questions" (default ON)
 * - DQ: "Autofill voluntary self-identification questions" (default OFF) — shown
 *   only in the ia_dq_questions_prefill_tst test bucket (=== 1); hidden for
 *   inactive/control (IAFE-2517, IAFE-2720).
 *
 * Figma: https://www.figma.com/design/YqvUoqcp7hUVEjilAGM0ls/Auto-Apply-2026?node-id=19431-64136
 * JIRA: IAFE-2080, IAFE-2517, IAFE-2720
 */
export const ApplicationQuestionsSection: React.FC<ApplicationQuestionsSectionProps> = ({
    sqOptedIn,
    onSqOptInChange,
    dqOptedIn,
    onDqOptInChange,
    showDqToggle,
}) => {
    const gt = GettextFactory();
    const location = useLocation();
    const eventLogger = useEventLogger();
    const sqSwitchId = useId();
    const dqSwitchId = useId();

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Flex sx={{ gap: 2, alignItems: 'center' }}>
                <Box sx={{ flexShrink: 0 }}>
                    <FileDoc size="md" aria-hidden />
                </Box>
                <Heading level={3} as="h2" sx={{ marginBlockEnd: 0 }}>
                    {gt.gettext('Application questions')}
                </Heading>
            </Flex>
            <Box
                sx={{
                    border: '1px solid',
                    borderColor: 'border.weak',
                    borderRadius: 'lg',
                    padding: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                }}
            >
                {/* SQ (screening question) prefill block - toggle (default ON),
                    helper text, and past-answers review link */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Flex
                        sx={{ gap: 2, alignItems: 'flex-start', justifyContent: 'space-between' }}
                    >
                        <Label htmlFor={sqSwitchId} sx={{ flex: 1, fontWeight: '700' }}>
                            {gt.gettext('Autofill application questions')}
                        </Label>
                        <Switch
                            as="span"
                            id={sqSwitchId}
                            checked={sqOptedIn}
                            sx={{ padding: 0 }}
                            onChange={(e) => onSqOptInChange(e.target.checked)}
                        />
                    </Flex>
                    <Text level={2} variant="secondary">
                        {gt.gettext(
                            'AI will autofill your applications using your past and future answers to employer questions. AI can make mistakes, so review before you continue.',
                        )}
                    </Text>
                    <Box>
                        <TextLink
                            href="/auto-apply-setup/past-answers?from=auto-apply-setup"
                            target="_blank"
                            rel="noopener"
                            iconAfter={<NewWindow aria-hidden />}
                            aria-label={gt.gettext(
                                'Review your past answers (opens in a new window)',
                            )}
                            sx={{ fontSize: 2 }}
                            onClick={() => {
                                eventLogger.logAutoApplySetupButtonClickEvent?.({
                                    currentPage: location.pathname,
                                    button: 'view-past-answers',
                                });
                            }}
                        >
                            {gt.gettext('Review your answers')}
                        </TextLink>
                    </Box>
                </Box>

                {/* DQ (voluntary self-identification) prefill block - toggle (default
                    OFF) and helper text. Gated on the ia_dq_questions_prefill_tst test
                    bucket (IAFE-2720). */}
                {showDqToggle && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Flex
                            sx={{
                                gap: 2,
                                alignItems: 'flex-start',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Label htmlFor={dqSwitchId} sx={{ flex: 1, fontWeight: '700' }}>
                                {gt.gettext('Autofill voluntary self-identification questions')}
                            </Label>
                            <Switch
                                as="span"
                                id={dqSwitchId}
                                checked={dqOptedIn}
                                sx={{ padding: 0 }}
                                onChange={(e) => onDqOptInChange(e.target.checked)}
                            />
                        </Flex>
                        <Text level={2} variant="secondary">
                            {gt.gettext(
                                'We will use your past and future answers on Indeed to autofill self-identification questions. Any question you answered will be shared separately from your applications and used for reporting only.',
                            )}
                        </Text>
                        <Box>
                            <TextLink
                                as="span"
                                iconAfter={<NewWindow aria-hidden />}
                                sx={{ fontSize: 2 }}
                            >
                                {gt.gettext('Review your answers')}
                            </TextLink>
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    );
};
